import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { LandingPage } from '@/pages/LandingPage';
import { useDrawerStore, type UrlFormData } from '@/stores/drawerStore';
import { useAuthStore } from '@/stores/authStore';
import { STORAGE_KEYS } from '@/constants/storage';
import { urlFormDataSchema } from '@/schemas/formDataSchema';

/**
 * 認証後に保存されたフォームデータを復元
 * 復元後は sessionStorage から削除
 *
 * Zod でバリデーションを行い、不正なデータを拒否
 */
function restorePendingFormData(): Partial<UrlFormData> | null {
  try {
    const savedData = sessionStorage.getItem(STORAGE_KEYS.PENDING_FORM_DATA);
    if (savedData) {
      sessionStorage.removeItem(STORAGE_KEYS.PENDING_FORM_DATA);
      const parsed = JSON.parse(savedData);

      // Zod でバリデーション
      const validated = urlFormDataSchema.safeParse(parsed);
      if (validated.success) {
        return validated.data;
      } else {
        console.warn('Invalid form data in sessionStorage:', validated.error);
        return null;
      }
    }
  } catch (error) {
    console.error('Failed to restore form data:', error);
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_FORM_DATA);
  }
  return null;
}

/**
 * 検索パラメータスキーマ
 * OAuth 認証後のリダイレクトで使用
 *
 * 注意: TanStack Router は URL パラメータを自動的に型変換するため、
 * `auth_success=true` は boolean として解析される。
 * z.coerce.string() を使用して string に変換する。
 */
const indexSearchSchema = z.object({
  auth_success: z.coerce.string().optional(),
  error: z.coerce.string().optional(),
});

/**
 * Index Route (/)
 * ランディングページを表示
 *
 * OAuth 認証後のリダイレクト処理:
 * - auth_success=true: 認証成功、drawer を開く
 * - error=*: 認証エラー、エラー表示
 */
export const Route = createFileRoute('/')({
  validateSearch: indexSearchSchema,
  beforeLoad: async ({ search }) => {
    // OAuth 認証成功後のリダイレクト処理
    if (search.auth_success === 'true') {
      // oauth_pending フラグを確認
      const oauthPending = sessionStorage.getItem(STORAGE_KEYS.OAUTH_PENDING);
      if (oauthPending) {
        sessionStorage.removeItem(STORAGE_KEYS.OAUTH_PENDING);

        try {
          // 認証状態を更新（結果を直接受け取ることでレースコンディションを回避）
          const authResult = await useAuthStore.getState().initializeAuth();

          if (authResult.success) {
            // 認証前に保存したフォームデータを復元
            const pendingFormData = restorePendingFormData();
            if (pendingFormData) {
              useDrawerStore.getState().setFormData(pendingFormData);
            }
            useDrawerStore.setState({ activeView: 'form' });
          } else if (authResult.error) {
            // エラーがある場合は auth ビューを開く（再ログイン促進）
            useDrawerStore.setState({ activeView: 'auth' });
            console.error('Auth initialization failed:', authResult.error);
          }
        } catch (error) {
          console.error('OAuth callback error:', error);
          // エラー時は auth ビューを開く
          useDrawerStore.setState({ activeView: 'auth' });
        }
      }

      // TanStack Router の redirect を使用してクリーンな URL にリダイレクト
      throw redirect({ to: '/', replace: true });
    }

    // エラー処理
    if (search.error) {
      console.error('OAuth error:', search.error);
      // エラー時は auth ビューを開く
      useDrawerStore.setState({ activeView: 'auth' });
      // TanStack Router の redirect を使用
      throw redirect({ to: '/', replace: true });
    }
  },
  component: LandingPage,
});
